import React, { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import rakeMovementService from '@/services/rakeMovementService';
import { useAuth } from '@/contexts/AuthContext';
import { CommonSearchInput } from '@/components/ui/common-search-input';
import { usePagination } from '@/hooks/usePagination';
import { useSearchParams } from 'react-router-dom';
import MissingWagonReceiptsTable from './MissingWagonReceiptsTable';
import ViewMissingWagonDetailsDialog from './dialogs/ViewMissingWagonDetailsDialog';
import AddMissingWagonReceiptDialog from './dialogs/AddMissingWagonReceiptDialog';

const MissingWagonReceiptsPage = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [totalTrips, setTotalTrips] = useState(0);

  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 10 });
  const { userProfile, userRole } = useAuth();
  const { toast } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
  const [isAddReceiptOpen, setIsAddReceiptOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const role = (userProfile?.role || userRole || '').toLowerCase();
  const isAdmin = role === 'ipl_super_admin' || role === 'ipl_admin';

  useEffect(() => {
    const search = searchParams.get('search') || '';
    setSearchQuery(search);
    fetchRakes(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchParams.get('search')]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const currentSearchParam = searchParams.get('search') || '';
      if (searchQuery !== currentSearchParam) {
        handleSearch(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchRakes = async (query = searchQuery) => {
    try {
      setLoading(true);

      let rrNumber = '';
      let tripNumber = '';
      if (query.trim()) {
        if (query.toUpperCase().startsWith('RR')) {
          rrNumber = query.trim();
        } else {
          tripNumber = query.trim();
        }
      }

      const fetchFn = isAdmin
        ? rakeMovementService.getRakeMovements.bind(rakeMovementService)
        : rakeMovementService.getMyRakeMovements.bind(rakeMovementService);

      const skip = (currentPage - 1) * pageSize;

      const response = await fetchFn({
        skip,
        limit: pageSize,
        rr_number: rrNumber,
        trip_number: tripNumber
      });

      const rawList = Array.isArray(response) ? response : response?.data || response?.items || [];
      const count = (response && typeof response === 'object' && !Array.isArray(response) && (response.total || response.count))
        ? (response.total || response.count)
        : (rawList.length < pageSize ? skip + rawList.length : skip + rawList.length + 1);

      setTrips(rawList);
      setTotalTrips(count);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch rake movements:', err);
      setError('Failed to load rake movements');
      toast({
        title: 'Error',
        description: 'Failed to load missing wagon receipts.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const newParams = new URLSearchParams(searchParams);

    if (query) {
      newParams.set('search', query);
    } else {
      newParams.delete('search');
    }

    newParams.set('page', '1');
    setPage(1);

    setSearchParams(newParams);
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleViewReceipts = (trip) => {
    setSelectedTrip(trip);
    setIsViewDetailsOpen(true);
  };

  const handleAddMissingWagon = (trip) => {
    setSelectedTrip(trip);
    setIsAddReceiptOpen(true);
  };

  const handleTopAddClick = () => {
    if (trips.length > 0) {
      setSelectedTrip(trips[0]);
    } else {
      setSelectedTrip(null);
    }
    setIsAddReceiptOpen(true);
  };

  const handleSubmitMissingWagon = async (payload, targetTripId) => {
    const tripId = targetTripId || selectedTrip?.id;
    if (!tripId) {
      toast({
        title: 'Error',
        description: 'Please select a valid rake trip.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      await rakeMovementService.addMissingWagonReceipt(tripId, payload);

      toast({
        title: 'Receipt Added',
        description: `Successfully recorded missing wagon receipt.`,
      });

      fetchRakes();
    } catch (err) {
      console.error('Failed to add missing wagon receipt:', err);
      toast({
        title: 'Error',
        description: err?.message || 'Failed to add missing wagon receipt.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>;
  }

  const totalPages = Math.ceil(totalTrips / pageSize) || 1;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">
            Missing Wagon Receipts
          </h1>
          <p className="text-xs text-muted-foreground">
            View history and record late-arriving missing wagon receipts for received rakes.
          </p>
        </div>
      </div>

      <div className="mb-4 w-full mt-4">
        <div className="md:w-1/3">
          <CommonSearchInput
            placeholder="Search by trip number or RR number..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            onSearch={handleSearch}
            maxLength={50}
          />
        </div>
      </div>

      <TableBlurLoader isLoading={loading} className="mt-4">
        <div
          className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
          style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}
        >
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {trips.length > 0 ? (
              <MissingWagonReceiptsTable
                trips={trips}
                currentPage={currentPage}
                totalPages={totalPages}
                totalTrips={totalTrips}
                pageSize={pageSize}
                onPageChange={setPage}
                onViewReceipts={handleViewReceipts}
                onAddMissingWagon={handleAddMissingWagon}
              />
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                {loading ? '' : 'No data available'}
              </div>
            )}
          </div>
        </div>
      </TableBlurLoader>

      <ViewMissingWagonDetailsDialog
        open={isViewDetailsOpen}
        onOpenChange={setIsViewDetailsOpen}
        trip={selectedTrip}
        onOpenAddModal={(trip) => {
          setSelectedTrip(trip);
          setIsAddReceiptOpen(true);
        }}
      />

      <AddMissingWagonReceiptDialog
        open={isAddReceiptOpen}
        onOpenChange={setIsAddReceiptOpen}
        trip={selectedTrip}
        tripsList={trips}
        onSelectTrip={setSelectedTrip}
        submitting={submitting}
        onSubmit={handleSubmitMissingWagon}
      />
    </div>
  );
};

export default MissingWagonReceiptsPage;
